import {
  Envelope,
  Phone,
  VideoCamera,
  MapPin,
  ArrowUpRight,
} from '@phosphor-icons/react';
import { usePublicProfileData } from '@/context/PublicProfileContext';
import { WhatsAppIcon } from '@/components/shared/WhatsAppIcon';
import type { ContactChannel } from '@/types/dashboard';

const isUrlChannel = (c: ContactChannel) => c === 'meeting' || c === 'location';

function contactHref(channel: ContactChannel, value: string) {
  if (channel === 'whatsapp')
    return `https://wa.me/${value.replace(/\D/g, '')}`;
  if (channel === 'phone') return `tel:${value.replace(/\s/g, '')}`;
  if (isUrlChannel(channel)) return value;
  return `mailto:${value}`;
}

function displayValue(channel: ContactChannel, value: string) {
  if (!isUrlChannel(channel)) return value;
  try {
    return new URL(value).hostname.replace(/^www\./, '');
  } catch {
    return value;
  }
}

export function Contact() {
  const { contacts } = usePublicProfileData();
  if (contacts.length === 0) return null;

  return (
    <>
      <div className="ap-divider" />
      <section id="contact">
        <h2 className="ap-section__title">Contact</h2>
        <div className="ap-contact">
          {contacts.map((c) => {
            const external =
              c.channel === 'whatsapp' || isUrlChannel(c.channel);
            return (
              <div key={c.id} className="ap-contact__row">
                <span className="ap-contact__row-label">{c.label}</span>
                <a
                  href={contactHref(c.channel, c.value)}
                  className="ap-contact__row-action"
                  target={external ? '_blank' : undefined}
                  rel={external ? 'noreferrer' : undefined}
                >
                  <span className="ap-contact__row-icon">
                    {c.channel === 'whatsapp' && <WhatsAppIcon size={15} />}
                    {c.channel === 'phone' && (
                      <Phone size={15} weight="regular" />
                    )}
                    {c.channel === 'email' && (
                      <Envelope size={15} weight="regular" />
                    )}
                    {c.channel === 'meeting' && (
                      <VideoCamera size={15} weight="regular" />
                    )}
                    {c.channel === 'location' && (
                      <MapPin size={15} weight="regular" />
                    )}
                  </span>
                  <span className="ap-contact__row-email">
                    {displayValue(c.channel, c.value)}
                  </span>
                  <ArrowUpRight
                    size={14}
                    weight="bold"
                    className="ap-contact__row-arrow"
                  />
                </a>
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}
